import { Slot } from "@radix-ui/react-slot";
import {
  forwardRef,
  isValidElement,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  useRef,
  useState,
} from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "core/shadcn/components/ui/tooltip";
import { cn } from "core/shadcn/lib/utils";

export type TooltipSide = "top" | "right" | "bottom" | "left";
export type TooltipAlign = "start" | "center" | "end";

// The trigger child receives the trigger's ref and event handlers, so it must
// be an element that forwards both: a DOM element, or a component built with
// forwardRef that spreads its props. Plain text gets a span to attach to.
// A disabled control fires no pointer events, so it sits in a focusable span
// that receives them instead, as in the Radix docs for disabled triggers.
function triggerFor(children: ReactNode): ReactElement {
  if (!isValidElement<{ disabled?: unknown }>(children)) {
    return <span>{children}</span>;
  }
  if (children.props.disabled === true) {
    return (
      <span className="inline-flex *:pointer-events-none" tabIndex={0}>
        {children}
      </span>
    );
  }
  return children;
}

type WithTooltipProps = {
  align?: TooltipAlign;
  children: ReactNode;
  /** Classes for the tooltip bubble, for example `max-w-none`. */
  className?: string;
  content: ReactNode;
  /** Milliseconds between hover and opening. */
  delay?: number;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  side?: TooltipSide;
  /** Pixels between the trigger and the bubble. */
  sideOffset?: number;
} & Omit<HTMLAttributes<HTMLElement>, "children" | "className" | "content">;

/**
 * Shows `content` in a tooltip when the child is hovered or focused. Empty
 * `content` shows no tooltip, so a tooltip can be switched off without
 * changing the tree.
 *
 * The ref and any other props land on the child, so another asChild trigger
 * (a popover, a menu) can wrap this the same way it wraps a button.
 */
export const WithTooltip = forwardRef<HTMLElement, WithTooltipProps>(
  function WithTooltip(
    {
      align,
      children,
      className,
      content,
      delay = 100,
      onOpenChange,
      open,
      side = "top",
      sideOffset = 4,
      ...triggerProps
    },
    ref,
  ) {
    // Empty content keeps the same tree and only keeps the tooltip shut, so
    // the child does not remount when content comes and goes. The root is
    // always controlled, so that switch never flips it between controlled
    // and uncontrolled.
    const empty =
      content === null ||
      content === undefined ||
      content === false ||
      content === "";
    const [hoverOpen, setHoverOpen] = useState(false);
    // Radix closes a tooltip when its trigger is clicked. antd kept it open,
    // and call sites rely on that to switch the content to a confirmation
    // such as "Copied". So a click ignores the close; leaving still closes it.
    const clicking = useRef(false);

    return (
      <Tooltip
        delayDuration={delay}
        open={!empty && (open ?? hoverOpen)}
        onOpenChange={(next) => {
          if (!next && clicking.current) {
            return;
          }
          setHoverOpen(next);
          if (!empty) {
            onOpenChange?.(next);
          }
        }}
      >
        <Slot ref={ref} {...triggerProps}>
          <TooltipTrigger
            asChild
            // Radix closes on pointer down and again on click, after these
            // run. The flag stays up until the click has finished.
            onPointerDown={() => {
              clicking.current = true;
            }}
            onClick={() => {
              setTimeout(() => {
                clicking.current = false;
              });
            }}
            onPointerLeave={() => {
              clicking.current = false;
            }}
          >
            {triggerFor(children)}
          </TooltipTrigger>
        </Slot>
        {!empty && (
          <TooltipContent
            side={side}
            align={align}
            sideOffset={sideOffset}
            // 1070 is where antd draws its tooltips, above its modals (1000),
            // popovers (1030) and dropdowns (1050), so a tooltip inside any of
            // them is not hidden behind it.
            className={cn("z-1070 max-w-64", className)}
          >
            {content}
          </TooltipContent>
        )}
      </Tooltip>
    );
  },
);
